import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const MIN_LEN = 1;
const MAX_LEN = 5000;

interface TicketComposerProps {
  disabled?: boolean;
  disabledReason?: string;
  placeholder?: string;
  submitLabel?: string;
  onSubmit: (body: string) => Promise<void>;
}

export function TicketComposer({
  disabled,
  disabledReason,
  placeholder = "Type your reply…",
  submitLabel = "Send reply",
  onSubmit,
}: TicketComposerProps) {
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const trimmed = body.trim();
  const canSubmit = !disabled && !submitting && trimmed.length >= MIN_LEN;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setBody("");
    } finally {
      setSubmitting(false);
    }
  };

  if (disabled) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
        {disabledReason ?? "Replies are disabled for this ticket."}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <Textarea
        rows={3}
        value={body}
        maxLength={MAX_LEN}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        className="border-0 focus-visible:ring-0 resize-none"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />
      <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
        <span className="text-xs text-slate-400">
          {trimmed.length}/{MAX_LEN} · ⌘/Ctrl + Enter to send
        </span>
        <Button
          type="button"
          size="sm"
          disabled={!canSubmit}
          onClick={handleSubmit}
          className="gap-2"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
